/**
 * Parse a pasted-or-uploaded CSV/list of email addresses.
 *
 * The capture-app side of the platform deals with high-precision sensor
 * data — but here we just need to pull email addresses out of whatever
 * shape the operator gives us. Accept:
 *   - one per line
 *   - comma-separated
 *   - semicolon-separated
 *   - whitespace-separated
 *   - mix of the above
 *
 * We don't validate the email format — the backend does that and
 * reports per-row results. We only normalise (trim, drop blanks,
 * dedupe within the submitted list).
 */

const SEPARATOR_REGEX = /[\s,;]+/;

export interface ParseResult {
  /** Unique trimmed entries in the order they first appeared. */
  emails: string[];
  /** Count of entries we collapsed because they appeared more than once. */
  duplicatesRemoved: number;
}

export function parseEmailList(input: string): ParseResult {
  const tokens = input.split(SEPARATOR_REGEX).map((t) => t.trim()).filter(Boolean);
  const seen = new Set<string>();
  const emails: string[] = [];
  let duplicatesRemoved = 0;
  for (const t of tokens) {
    const key = t.toLowerCase();
    if (seen.has(key)) {
      duplicatesRemoved += 1;
      continue;
    }
    seen.add(key);
    emails.push(t);
  }
  return { emails, duplicatesRemoved };
}
